import { AuthProvider } from '@auth/auth-provider'
import { ErrorBoundary } from '@components/error-boundary'
import { CloseNotificationBtn } from '@components/notification/close-notification-btn'
import { Notification } from '@components/notification/notification'
import { queryClient } from '@config/query-client'
import { theme } from '@config/theme'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useIsTablet } from '@utils/hooks/use-is-tablet'
import { isInIframe } from '@utils/is-in-iframe'
import { sanitizeCurrentLocationForHistory } from '@utils/sanitize-url'
import { SnackbarProvider } from 'notistack'
import { Suspense } from 'react'
import { BrowserRouter } from 'react-router-dom'

import { IframeRoutes } from './routes/iframe-routes'
import { StandaloneRoutes } from './routes/standalone-routes'

if (isInIframe) {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')

  if (token) {
    document.cookie = `access_token=${token}; path=/; secure; samesite=None`

    const sanitizedPath = sanitizeCurrentLocationForHistory(window.location)
    if (sanitizedPath) {
      window.history.replaceState(window.history.state, '', sanitizedPath)
    }
  }
}

export const App = () => {
  const isTablet = useIsTablet()

  return (
    <ErrorBoundary>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: isTablet ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        action={(snackbarKey) => (
          <CloseNotificationBtn snackbarKey={snackbarKey} />
        )}
        Components={{
          error: Notification,
          success: Notification,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <div
            style={{
              height: '100dvh',
              width: '100vw',
              position: 'relative',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <BrowserRouter>
                  <AuthProvider>
                    {isInIframe ? <IframeRoutes /> : <StandaloneRoutes />}
                  </AuthProvider>
                </BrowserRouter>
              </ThemeProvider>
            </LocalizationProvider>
          </div>
          {/* {import.meta.env.MODE !== 'production' && (
            <Suspense fallback={null}>
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
              />
            </Suspense>
          )} */}
        </QueryClientProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  )
}
